import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, XCircle } from "lucide-react";
import { SiteHeader } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { redeemSso, setAuth, getAuth } from "@/lib/authApi";

// Landing for the PBO app → fantasy site handoff:
//   /sso?token=<90s one-shot>&tournament=<id>&room=<code>
// The token is minted only when a member taps "Fantasy Draft" inside the PBO
// app (never emailed), so it's safe to redeem on load. A refresh after success
// must NOT re-verify — we remember redeemed tokens in sessionStorage.
export default function Sso() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token");
  const started = useRef(false);
  const [error, setError] = useState(token ? "" : "Missing sign-in token.");

  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    const key = `pbo-sso-${token}`;
    if (sessionStorage.getItem(key) && getAuth()?.user) {
      nav("/my-contests", { replace: true });
      return;
    }
    redeemSso(token)
      .then(({ session_token, user }) => {
        setAuth({ session_token, user });
        sessionStorage.setItem(key, "1");
        toast.success(`Welcome, ${user.name || user.email}`);
        nav("/my-contests", { replace: true });
      })
      .catch((e) => {
        setError(e?.response?.data?.detail || "Couldn't sign you in from PBO.");
      });
  }, [token, nav]);

  return (
    <div className="min-h-screen bg-pbo-paper">
      <SiteHeader />
      <div className="max-w-md mx-auto px-6 py-16">
        <div
          className="bg-white border border-pbo-border rounded-2xl p-8 shadow-sm text-center"
          data-testid={error ? "sso-error" : "sso-verifying"}
        >
          {!error ? (
            <>
              <Loader2 className="w-10 h-10 text-pbo-green mx-auto mb-4 animate-spin" />
              <h1 className="font-serif text-pbo-green text-2xl mb-2">
                Signing you in from PBO…
              </h1>
              <p className="text-pbo-ink/70 text-sm">One moment.</p>
            </>
          ) : (
            <>
              <XCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
              <h1 className="font-serif text-pbo-green text-2xl mb-2">
                Sign-in didn&apos;t go through
              </h1>
              <p className="text-pbo-ink/70 text-sm mb-6" data-testid="sso-error-msg">
                {error}
              </p>
              <Button
                onClick={() => nav("/login")}
                className="bg-pbo-green hover:bg-pbo-green-deep text-pbo-cream rounded-full"
                data-testid="sso-email-login-btn"
              >
                Sign in with email instead
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
